import {FileTextOutlined, FilterOutlined, LoadingOutlined} from "@ant-design/icons";
import Collapse from "@kunukn/react-collapse";
import {Alert, Breadcrumb, Button, Descriptions, Form, Input, message, Modal, Select, Spin, Tag, Tooltip} from "antd";
import type {InputRef} from "antd/es/input";
import {useForm} from "antd/es/form/Form";
import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {Link, useNavigate, useParams} from "react-router-dom";
import {AppContextContext, ClientNotificationServiceContext, ItemUpdateProfileServiceContext, VulReportServiceContext, VulSourceItemServiceContext} from "../../Contexts";
import VulReport from "../../domain/VulReport";
import VulReportAiSummary, {VulReportAiSummaryGroup, VulReportAiSummaryStatus} from "../../domain/VulReportAiSummary";
import {ServerConstraintViolationsHolder} from "../../sal-ui/ServerConstraintViolations";
import * as globalStyles from "../App.module.css";
import * as styles from "./VulReportDetail.module.css";
import {DocumentTitle} from "../DocumentTitle";
import dayjs from "dayjs";
import {formatDateTime, formatInteger} from "../../utils/FormatUtils";
import {TableUiConfig, useTableHandler} from "../../sal-ui/TableHandler";
import CreateTicketModal from "../ticket/CreateTicketModal";
import PagedResult from "../../service/PagedResult";
import VulSourceItem from "../../domain/VulSourceItem";
import VulView from "../../domain/VulView";
import {Permission} from "../../domain/auth/Permission";
import {ResourceOwnerType} from "../../domain/ResourceOwnerType";
import {Feature} from "../../domain/Feature";
import QueryOptions from "../../sal-ui/QueryOptions";
import ItemUpdateProfile, {itemUpdateProfileSystemLastComparator} from "../../domain/ItemUpdateProfile";
import {FilterItem, FilterItemProps, SelectEditor} from "../tablefilter/TableFilter";
import {SearchFilter} from "../tablefilter/SearchFilter";
import {FormPersister} from "../../sal-ui/FormPersister";
import MarkdownContent from "../common/MarkdownContent";
import AiRunMetrics from "../common/AiRunMetrics";
import AiProviderCommunicationLogModal from "../common/AiProviderCommunicationLogModal";
import AiReportSummarizerSelect from "../aireportsummarizer/AiReportSummarizerSelect";
import VulReportItems from "./VulReportItems";
import {InterestFeedbackOption} from "../../domain/InterestMemory";

const serverViolationsHolder = new ServerConstraintViolationsHolder();

const defaultVisibleColumns = ["id", "description", "attributes", "sourcePublished"];

const defaultTableUiConfig = {immediateMode: false, visibleColumns: defaultVisibleColumns, showAiAnalytics: true, showAiSummaryGroups: true};
const aiSummaryGenerationCompletedEvent = "seal:VUL_REPORT_AI_SUMMARY_GENERATION_COMPLETED";
const aiSummaryStatusPresentation: Record<VulReportAiSummaryStatus, { color: string, label: string }> = {
    DONE: {color: "green", label: "done"},
    FAILED: {color: "red", label: "failed"},
    PENDING: {color: "blue", label: "pending"},
    RUNNING: {color: "blue", label: "running"},
    NOT_GENERATED: {color: "default", label: "not generated"}
};
const reportItemTypeOptions = [
    {value: "NEW", label: "New"},
    {value: "MODIFIED", label: "Modified"}
];
const AI_SUMMARY_UNGROUPED_GROUP_VALUE = "ungrouped";
const AI_SUMMARY_GROUP_VALUE_PREFIX = "group:";
const AI_SUMMARY_GROUP_VALUE_PATTERN = new RegExp(`^${AI_SUMMARY_GROUP_VALUE_PREFIX}\\d+$`);

type ReportItemsFilterValues = {
    profilePreviewId?: string;
    itemTypes?: string[];
    aiSummaryGroup?: string;
};

type AiSummaryGroupOption = {
    value: string;
    label: string;
    title: string;
    itemCount: number;
    itemIds?: string[];
    basis?: string;
    ungrouped?: boolean;
};

type VisibleAiSummaryGroup = {
    group: VulReportAiSummaryGroup;
    index: number;
};

const defaultReportItemsFilterValues: ReportItemsFilterValues = {};

function VulReportDetailContent() {
    const persistentIdent = 'VulReportDetail';
    const reportItemsFilterPersister = useMemo(() => new FormPersister(`${persistentIdent}.itemsFilter`), []);
    const appContext = useContext(AppContextContext);
    const vulReportService = useContext(VulReportServiceContext);
    const vulSourceItemService = useContext(VulSourceItemServiceContext);
    const itemUpdateProfileService = useContext(ItemUpdateProfileServiceContext);
    const clientNotificationService = useContext(ClientNotificationServiceContext);
    const navigate = useNavigate();
    const {reportId}: any = useParams();
    const tableHandler = useTableHandler("sourcePublished desc", {
        reloadFunction: reloadItems,
        initialPageSize: 25,
        persistentIdent: `${persistentIdent}`
    });
    const [feedbackInterests, setFeedbackInterests] = useState<InterestFeedbackOption[]>([]);
    const itemsReloadSequence = useRef(0);
    const canRefineInterest = appContext.user.isSystemAdmin() || appContext.user.hasPermission(Permission.INTEREST__MANAGE_ORG);
    const [editMode, setEditMode] = useState(false);
    const [sendReportMode, setSendReportMode] = useState(false);
    const [report, setReport] = useState<VulReport>();
    const [editForm] = useForm();
    const [sendReportForm] = useForm();
    const sendReportTargetInputRef = useRef<InputRef>(null);
    const [vulSourceItems, setVulSourceItems] = useState<PagedResult<VulSourceItem>>();
    const [tableUiConfig, setTableUiConfig] = useState<TableUiConfig>(defaultTableUiConfig);
    const [createTicketFormVisible, setCreateTicketFormVisible] = useState<boolean>(false);
    const [vulSourceItem, setVulSourceItem] = useState<VulSourceItem>();
    const [itemCount, setItemCount] = useState<{ total: number, newItems: number, modifiedItems: number }>();
    const [itemUpdateProfiles, setItemUpdateProfiles] = useState<ItemUpdateProfile[]>([]);
    const [reportItemsFilterValues, setReportItemsFilterValues] = useState<ReportItemsFilterValues>(() => normalizeReportItemsFilterValues(reportItemsFilterPersister.loadValues()));
    const [aiSummary, setAiSummary] = useState<VulReportAiSummary>();
    const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
    const [aiSummaryRegenerating, setAiSummaryRegenerating] = useState(false);
    const [aiSummaryDialogOpen, setAiSummaryDialogOpen] = useState(false);
    const [manualSummarizerId, setManualSummarizerId] = useState<string | null>();
    const [aiProviderCommunicationLogRunId, setAiProviderCommunicationLogRunId] = useState<string | undefined>();
    const profilePreviewId = reportItemsFilterValues.profilePreviewId;
    const reportItemTypes = reportItemsFilterValues.itemTypes;
    const aiSummaryGroup = reportItemsFilterValues.aiSummaryGroup;
    const aiSummaryGroupOptions = useMemo(() => buildAiSummaryGroupOptions(aiSummary), [aiSummary]);
    const canUseAiSummary = appContext.user.hasFeature(Feature.AI);
    const automaticSummarizer = report?.definition?.autoGenerateAiSummary ? report.definition.aiReportSummarizer : undefined;
    const selectedSummarizerId = manualSummarizerId === undefined ? automaticSummarizer?.id : manualSummarizerId ?? undefined;

    useEffect(() => () => {
        itemsReloadSequence.current++;
    }, []);

    const layout = {
        labelCol: {span: 6},
        wrapperCol: {span: 18},
    };

    const tailLayout = {
        wrapperCol: {offset: 6, span: 16},
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        reload();
        itemUpdateProfileService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(value => {
            setItemUpdateProfiles(value.data.sort(itemUpdateProfileSystemLastComparator));
        });

        setTableUiConfig({...defaultTableUiConfig, ...tableHandler.loadUiConfig()});
    }, [])

    useEffect(() => {
        if (report !== undefined) {
            tableHandler.resetPage();
            tableHandler.reload();
        }
    }, [report, profilePreviewId, reportItemTypes, aiSummaryGroup, tableUiConfig.showAiSummaryGroups, aiSummaryGroupOptions, canRefineInterest]);

    useEffect(() => {
        if (!aiSummaryGroup || aiSummaryGroupOptions.some(option => option.value === aiSummaryGroup)) {
            return;
        }

        const values = {...reportItemsFilterValues};

        delete values.aiSummaryGroup;
        onReportItemsFilterChange(values);
    }, [aiSummaryGroup, aiSummaryGroupOptions]);

    useEffect(() => {
        if (!canUseAiSummary || !report || !report.id) {
            return;
        }

        clientNotificationService.subscribe(
            aiSummaryGenerationCompletedEvent,
            () => reloadAiSummary(report, false),
            report.id
        );

        return () => clientNotificationService.unsubscribe(aiSummaryGenerationCompletedEvent, report.id);
    }, [canUseAiSummary, report?.id]);

    return (
        <DocumentTitle title={report?.name}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}>
                    <Breadcrumb.Item>{appContext.config?.appName}</Breadcrumb.Item>
                    <Breadcrumb.Item><Link to={"/vul-reports"}>Vulnerability reports</Link></Breadcrumb.Item>
                    <Breadcrumb.Item>{report?.name}</Breadcrumb.Item>
                </Breadcrumb>

                <Spin spinning={report === undefined} indicator={<LoadingOutlined style={{fontSize: 24}} spin={true}/>}>
                    <h1>{report?.name}</h1>

                    <div className={globalStyles["common__top-button-bar"]}>
                        {appContext.user.hasPermission(Permission.REPORT__MANAGE) &&
                            <>
                                <Button onClick={() => setEditMode(!editMode)}>Edit</Button>

                                <Button danger={true}
                                        title={"Delete"}
                                        onClick={() => {
                                            Modal.confirm({
                                                content: "Do you really want to delete this report?",
                                                okText: "Delete",
                                                cancelText: "Cancel",
                                                okButtonProps: {danger: true},
                                                onOk: () => onDeleteConfirm()
                                            });
                                        }}>Delete</Button>

                                <Button title={"Regenerate report"}
                                        onClick={() => {
                                            Modal.confirm({
                                                content: "Do you really want to regenerate this report? This should be done if there are new vulnerability items for defined timestamp range or if vulnerability views definitions were updated from last report generation.",
                                                okText: "Regenerate",
                                                cancelText: "Cancel",
                                                onOk: () => onRegenerateConfirm()
                                            });
                                        }}>Regenerate report</Button>
                            </>
                        }

                        <Button onClick={() => setSendReportMode(!sendReportMode)}>Send report</Button>

                        {appContext.user.hasPermission(Permission.REPORT__MANAGE) && <Button title={"Clone"} onClick={() => navigate(`/vul-reports/clone/${report.id}`)}>Clone</Button>}
                    </div>

                    {renderEditForm()}
                    {renderSendReportForm()}

                    <Descriptions column={1} bordered={true} size="small" className={globalStyles.details}>
                        {appContext.user.isSystemAdmin() && <Descriptions.Item label={"Organization"}>
                            <Link to={`/organizations/${report?.organization?.id}`}>{report?.organization?.name}</Link>
                        </Descriptions.Item>}

                        {report?.definition && <Descriptions.Item label={"Vulnerability report definition"}><Link
                            to={`/definitions/vul-reports/${report?.definition?.id}`}>{report?.definition?.name}</Link></Descriptions.Item>}
                        {(appContext.user.hasPermission(Permission.VIEW__READ_ORG) || appContext.user.hasPermission(Permission.VIEW__READ_SEAL)) &&
                            <Descriptions.Item label={"Vulnerability views"}>
                                {renderVulViews()}
                            </Descriptions.Item>
                        }

                        <Descriptions.Item label={"Date range"}>
                            {formatDateTime(report?.timestampFrom)} - {formatDateTime(report?.timestampTo)} ({report?.timeZone})
                        </Descriptions.Item>

                        <Descriptions.Item label={"Generated at"}>
                            {formatDateTime(report?.generatedAt)}
                        </Descriptions.Item>
                    </Descriptions>

                    {canUseAiSummary && renderAiSummary()}

                    <CreateTicketModal
                        visible={createTicketFormVisible}
                        onTicketCreate={() => setCreateTicketFormVisible(false)}
                        vulSourceItem={vulSourceItem}
                        onCancel={() => setCreateTicketFormVisible(false)}
                    />

                    <AiProviderCommunicationLogModal
                        open={!!aiProviderCommunicationLogRunId}
                        runId={aiProviderCommunicationLogRunId}
                        onClose={() => setAiProviderCommunicationLogRunId(undefined)}
                    />

                    {report && <VulReportItems
                        reportId={report.id}
                        interests={feedbackInterests}
                        persistentIdent={persistentIdent}
                        items={vulSourceItems}
                        tableHandler={tableHandler}
                        tableUiConfig={tableUiConfig}
                        setTableUiConfig={setTableUiConfig}
                        aiSummaryGroups={aiSummaryGroupOptions}
                    >
                        <h2>Vulnerability items{itemCount && ` (${itemCount.newItems} new, ${itemCount.modifiedItems} modified)`}</h2>

                        {renderReportItemsFilter()}
                    </VulReportItems>}
                </Spin>
            </>
        </DocumentTitle>
    )

    function renderEditForm() {
        return (
            <Collapse isOpen={editMode}>
                <h3>Edit of vulnerability report</h3>

                <Form {...layout} form={editForm} className={`${globalStyles["common__form"]} ${globalStyles["common_form--edit"]}`} onFinish={onFinishEdit}>
                    <Form.Item
                        name={"name"}
                        label={"Name"}
                        rules={[
                            {required: true, message: "Name is required."}
                        ]}>
                        <Input autoFocus={true} maxLength={100}/>
                    </Form.Item>

                    <Form.Item {...tailLayout} className={globalStyles["common__form-buttons"]}>
                        <Button type={"primary"} htmlType={"submit"}>{"Save"}</Button>
                        <Button onClick={() => setEditMode(false)}>{"Cancel"}</Button>
                    </Form.Item>
                </Form>
            </Collapse>
        )
    }

    function renderAiSummary() {
        const status = aiSummary?.status ?? "NOT_GENERATED";
        const isRunning = aiSummary?.isRunning() ?? false;
        const isStale = status === "DONE" && aiSummary?.stale === true;

        return (
            <section className={styles["ai-summary"]}>
                <div className={styles["ai-summary-header"]}>
                    <h2>AI summary</h2>
                    <div className={styles["ai-summary-actions"]}>
                        {renderAiSummaryStatus(status, isStale)}
                        {appContext.user.isSystemAdmin() && aiSummary?.aiProviderCommunicationRunId &&
                            <Tooltip title={"Show AI provider calls"}>
                                <Button
                                    icon={<FileTextOutlined/>}
                                    aria-label={"Show AI provider calls"}
                                    onClick={() => setAiProviderCommunicationLogRunId(aiSummary.aiProviderCommunicationRunId!)}
                                />
                            </Tooltip>
                        }
                        {appContext.user.hasPermission(Permission.REPORT__MANAGE) &&
                            <Button
                                loading={aiSummaryRegenerating}
                                disabled={!report || isRunning}
                                onClick={() => setAiSummaryDialogOpen(true)}>
                                Regenerate summary
                            </Button>
                        }
                    </div>
                </div>

                <Modal
                    title="Generate AI summary"
                    open={aiSummaryDialogOpen}
                    okText="Generate"
                    cancelText="Cancel"
                    confirmLoading={aiSummaryRegenerating}
                    okButtonProps={{disabled: !report || isRunning}}
                    onOk={onRegenerateAiSummaryConfirm}
                    onCancel={() => setAiSummaryDialogOpen(false)}>
                    <Form layout="vertical">
                        <Form.Item label="AI report summarizer" extra="Leave empty to use the global default.">
                            <AiReportSummarizerSelect
                                organizationId={report?.organization?.id}
                                currentSummarizer={automaticSummarizer}
                                value={selectedSummarizerId}
                                onChange={value => setManualSummarizerId(value ?? null)}
                            />
                        </Form.Item>
                    </Form>
                </Modal>

                <Spin spinning={aiSummaryLoading || isRunning} indicator={<LoadingOutlined style={{fontSize: 20}} spin={true}/>}>
                    <div className={styles["ai-summary-body"]}>
                        {renderAiSummaryMeta()}
                        {status === "NOT_GENERATED" && <div className={styles["ai-summary-empty"]}>No AI summary has been generated for this report.</div>}
                        {status === "PENDING" && <div className={styles["ai-summary-empty"]}>AI summary generation is queued.</div>}
                        {status === "RUNNING" && <div className={styles["ai-summary-empty"]}>AI summary generation is running.</div>}
                        {status === "FAILED" && <Alert type="error" showIcon={true} title="AI summary generation failed" description={aiSummary?.errorMessage}/>}
                        {isStale &&
                            <Alert
                                className={styles["ai-summary-stale"]}
                                type="warning"
                                showIcon={true}
                                title="AI summary is stale"
                                description="This summary was generated for a previous report snapshot. Regenerate it before using it."
                            />
                        }
                        {status === "DONE" && (
                            <MarkdownContent className={styles["ai-summary-text"]}>
                                {aiSummary?.summary || ""}
                            </MarkdownContent>
                        )}
                        {status === "DONE" && renderAiSummaryGroups()}
                    </div>
                </Spin>
            </section>
        );
    }

    function renderVulViews() {
        const views = report?.vulViews ?? [];

        if (views.length === 0) {
            return <i>no vulnerability views</i>;
        }

        return views.map(view => <div key={view.id}>{renderVulView(view)}</div>);
    }

    function renderVulView(view: VulView) {
        if (canReadVulView(view)) {
            return <Link to={`/vul-source-items/views/${view.id}`}>{view.name}</Link>;
        }

        return <span>{view.name}</span>;
    }

    function canReadVulView(view: VulView) {
        switch (view.owner) {
            case ResourceOwnerType.ORGANIZATION:
                return appContext.user.hasPermission(Permission.VIEW__READ_ORG);
            case ResourceOwnerType.SYSTEM:
                return appContext.user.hasPermission(Permission.VIEW__READ_SEAL);
            default:
                return false;
        }
    }

    function renderAiSummaryStatus(status: VulReportAiSummaryStatus, stale: boolean) {
        const presentation = stale
            ? {color: "orange", label: "stale"}
            : aiSummaryStatusPresentation[status];

        return <Tag color={presentation.color}>{presentation.label}</Tag>;
    }

    function renderAiSummaryGroups() {
        const groupOptions = aiSummaryGroupOptions;

        if (groupOptions.length === 0) {
            return null;
        }

        const summaryGroups = groupOptions.filter(group => !group.ungrouped);
        const groupedItemCount = summaryGroups.reduce((sum, group) => sum + group.itemCount, 0);
        const ungroupedItemCount = groupOptions.find(group => group.ungrouped)?.itemCount ?? 0;
        const groupCount = summaryGroups.length;

        return (
            <div className={styles["ai-summary-groups"]}>
                <div className={styles["ai-summary-groups-header"]}>
                    <span>Groups</span>
                    <span>
                        {formatInteger(groupCount)} groups, {formatInteger(groupedItemCount)} grouped
                        {ungroupedItemCount > 0 && `, ${formatInteger(ungroupedItemCount)} ungrouped`}
                    </span>
                </div>
                <div className={styles["ai-summary-group-list"]}>
                    {groupOptions.map(group => (
                        <div className={styles["ai-summary-group"]} key={group.value}>
                            <div className={styles["ai-summary-group-title"]}>
                                <span className={styles["ai-summary-group-name"]}>{group.title}</span>
                                <Tag>{formatInteger(group.itemCount)} items</Tag>
                                <Button
                                    className={styles["ai-summary-group-filter-button"]}
                                    size="small"
                                    type="text"
                                    icon={<FilterOutlined/>}
                                    title="Filter by this group"
                                    aria-label="Filter by this group"
                                    disabled={aiSummaryGroup === group.value}
                                    onClick={() => onAiSummaryGroupFilterClick(group.value)}
                                />
                            </div>
                            {group.basis && <div className={styles["ai-summary-group-basis"]}>{group.basis}</div>}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    function renderAiSummaryMeta() {
        if (!aiSummary || aiSummary.status === "NOT_GENERATED") {
            return null;
        }

        const metaItems = [
            {label: "Model", value: aiSummary.model},
            {label: "Items", value: aiSummary.inputItemCount != null ? formatInteger(aiSummary.inputItemCount) : undefined},
            {label: "API calls", value: aiSummary.apiCallCount != null ? formatInteger(aiSummary.apiCallCount) : undefined},
            {label: "Web searches", value: aiSummary.webSearchCallCount != null ? formatInteger(aiSummary.webSearchCallCount) : undefined}
        ].filter(item => item.value != null);
        const hasRunMetrics = aiSummary.summarizerName || aiSummary.finishedAt || aiSummary.tokenUsage
            || aiSummary.executionTimeMs != null || aiSummary.priceBreakdown || aiSummary.price != null;

        return (
            <>
                {hasRunMetrics &&
                    <div className={styles["ai-summary-run-header"]}>
                        {aiSummary.summarizerName && <div className={styles["ai-summary-summarizer-name"]}>{aiSummary.summarizerName}</div>}
                        <AiRunMetrics
                            timestampLabel={aiSummary.finishedAt ? "Finished" : undefined}
                            timestamp={aiSummary.finishedAt}
                            tokenUsage={aiSummary.tokenUsage}
                            executionTimeMs={aiSummary.executionTimeMs}
                            priceBreakdown={aiSummary.priceBreakdown}
                            price={aiSummary.price}
                        />
                    </div>
                }
                {metaItems.length > 0 &&
                    <div className={styles["ai-summary-config"]}>
                        {metaItems.map(item => (
                            <span key={item.label} className={styles["ai-summary-config-item"]}>
                                <span className={styles["ai-summary-config-label"]}>{item.label}:</span>
                                <span>{item.value}</span>
                            </span>
                        ))}
                    </div>
                }
            </>
        );
    }

    function renderSendReportForm() {
        return (
            <Collapse
                isOpen={sendReportMode}
                onChange={({state}) => {
                    if (state === "expanded") {
                        sendReportTargetInputRef.current?.focus();
                    }
                }}>
                <h3>Send report</h3>

                <Form {...layout} form={sendReportForm} className={`${globalStyles["common__form"]} ${globalStyles["common_form--edit"]}`} onFinish={onSendReport}>

                    <Form.Item
                        name={"targetType"}
                        label={"Notification type"}
                        initialValue={"EMAIL"}>
                        <Select>
                            <Select.Option value={"EMAIL"}>E-Mail</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name={"target"}
                        label={"Destination"}
                        rules={[
                            {required: true, message: "Destination is required."}
                        ]}>
                        <Input ref={sendReportTargetInputRef} maxLength={100}/>
                    </Form.Item>

                    <Form.Item {...tailLayout} className={globalStyles["common__form-buttons"]}>
                        <Button type={"primary"} htmlType={"submit"}>{"Send"}</Button>
                        <Button onClick={() => setSendReportMode(false)}>{"Cancel"}</Button>
                    </Form.Item>
                </Form>
            </Collapse>
        )
    }

    function renderReportItemsFilter() {
        const items: FilterItem[] = [];

        if (report?.definition) {
            items.push({
                name: "profilePreviewId",
                key: "profilePreviewId",
                label: "Profile preview",
                render: renderProfilePreviewEditor
            });
        }

        items.push({
            name: "itemTypes",
            key: "itemTypes",
            label: "Type",
            render: renderReportItemTypeEditor
        });

        if (aiSummaryGroupOptions.length > 0) {
            items.push({
                name: "aiSummaryGroup",
                key: "aiSummaryGroup",
                label: "Group",
                render: renderAiSummaryGroupEditor
            });
        }

        return (
            <SearchFilter
                items={items}
                values={reportItemsFilterValues}
                defaultValues={defaultReportItemsFilterValues}
                persistentStateIdent={reportItemsFilterPersister.ident}
                defaultActiveFilterNames={["aiSummaryGroup", "itemTypes"]}
                immediateMode={tableUiConfig.immediateMode}
                onChange={onReportItemsFilterChange}
            />
        );
    }

    function renderProfilePreviewEditor(itemProps: FilterItemProps) {
        return (
            <SelectEditor
                multiple={false}
                showSearch={true}
                width={240}
                placeholder="Stored report"
                value={itemProps.value}
                onChange={itemProps.onChange}
                options={itemUpdateProfiles.map(profile => ({
                    value: profile.id,
                    label: profile.name
                }))}
            />
        );
    }

    function renderReportItemTypeEditor(itemProps: FilterItemProps) {
        return (
            <SelectEditor
                multiple={true}
                width={160}
                popupMatchSelectWidth={false}
                value={itemProps.value}
                onChange={itemProps.onChange}
                options={reportItemTypeOptions}
            />
        );
    }

    function renderAiSummaryGroupEditor(itemProps: FilterItemProps) {
        return (
            <SelectEditor
                multiple={false}
                showSearch={true}
                width={300}
                popupMatchSelectWidth={false}
                value={itemProps.value}
                onChange={itemProps.onChange}
                options={aiSummaryGroupOptions.map(option => ({
                    value: option.value,
                    label: option.label
                }))}
            />
        );
    }

    function onReportItemsFilterChange(values: unknown) {
        const normalizedValues = normalizeReportItemsFilterValues(values);

        reportItemsFilterPersister.saveState(normalizedValues);
        setReportItemsFilterValues(normalizedValues);
    }

    function onAiSummaryGroupFilterClick(groupValue: string) {
        onReportItemsFilterChange({
            ...reportItemsFilterValues,
            aiSummaryGroup: groupValue
        });
    }

    function onSendReport(values: any) {
        vulReportService.sendReport(report, values.targetType, values.target)
            .then(() => {
                message.success("Vulnerability report sent.");

                setSendReportMode(false);
            });
    }

    function onFinishEdit(values: any) {
        vulReportService.update(reportId, values)
            .then(
                () => {
                    message.success("Vulnerability report updated.");

                    setEditMode(false);

                    reload();
                },
                error => serverViolationsHolder.handleServerError(error, editForm)
            );
    }

    function onDeleteConfirm() {
        vulReportService.delete(report)
            .then(() => {
                message.success("Vulnerability report deleted.");

                navigate(`/vul-reports`);
            });
    }

    function onRegenerateConfirm() {
        vulReportService.regenerateItems(report)
            .then(() => {
                message.success("Vulnerability report items regenerated.");

                reload();
            });
    }

    function onRegenerateAiSummaryConfirm() {
        setManualSummarizerId(selectedSummarizerId ?? null);
        setAiSummaryRegenerating(true);

        vulReportService.regenerateAiSummary(report, selectedSummarizerId)
            .then(value => {
                setAiSummary(value);

                setAiSummaryDialogOpen(false);
                message.success("AI summary generation queued.");
            })
            .catch(error => {
                if (error.response?.status === 400) {
                    message.error(error.response.data?.message || "AI summary generation could not be queued.");
                }
            })
            .finally(() => setAiSummaryRegenerating(false));
    }

    function reload() {
        vulReportService.get(reportId).then(report => {
            setReport(report);

            if (canUseAiSummary) {
                reloadAiSummary(report, true);
            } else {
                setAiSummary(undefined);
            }

            editForm.setFieldsValue({
                ...report,
                timestampFrom: dayjs(report.timestampFrom),
                timestampTo: dayjs(report.timestampTo),
                vulViews: report.vulViews.map(value => value.id)
            });
        });
    }

    function reloadAiSummary(report: VulReport, showLoading: boolean) {
        if (showLoading) {
            setAiSummaryLoading(true);
        }

        vulReportService.getAiSummary(report)
            .then(value => setAiSummary(value))
            .finally(() => {
                if (showLoading) {
                    setAiSummaryLoading(false);
                }
            });
    }

    function reloadItems() {
        if (!report) {
            return Promise.resolve();
        }

        const reloadSequence = ++itemsReloadSequence.current;
        const effectiveProfilePreviewId = report.definition ? profilePreviewId : undefined;

        return Promise.all([
            vulSourceItemService.getListForReport(
                tableHandler.queryOptions,
                report,
                effectiveProfilePreviewId,
                reportItemTypes,
                aiSummaryGroup,
                (tableUiConfig.showAiSummaryGroups ?? false) && aiSummaryGroupOptions.length > 0
            ),
            // Header counts describe the stored report, not the current table filter or profile preview.
            vulReportService.countItems(report),
            canRefineInterest ? vulReportService.getInterestFeedback(report.id) : Promise.resolve<InterestFeedbackOption[]>([])
        ]).then(([value, count, interests]) => {
            if (reloadSequence !== itemsReloadSequence.current) {
                return;
            }

            tableHandler.updateTotal(value.total);

            setVulSourceItems(value);
            setFeedbackInterests(interests);
            setItemCount({
                total: count.newItems + count.modifiedItems,
                newItems: count.newItems,
                modifiedItems: count.modifiedItems
            });
        }).catch(() => {
            if (reloadSequence === itemsReloadSequence.current) {
                message.error("Unable to refresh report items and interest selection. Try again.");
            }
        });
    }

}

function normalizeReportItemsFilterValues(values: unknown): ReportItemsFilterValues {
    const sourceValues = values as Partial<Record<keyof ReportItemsFilterValues | "itemType", unknown>> | undefined;
    const normalizedValues: ReportItemsFilterValues = {};

    if (typeof sourceValues?.profilePreviewId === "string" && sourceValues.profilePreviewId.length > 0) {
        normalizedValues.profilePreviewId = sourceValues.profilePreviewId;
    }

    const itemTypes = normalizeReportItemTypes(sourceValues?.itemTypes ?? sourceValues?.itemType);

    if (itemTypes.length > 0) {
        normalizedValues.itemTypes = itemTypes;
    }

    if (typeof sourceValues?.aiSummaryGroup === "string" && isValidAiSummaryGroupValue(sourceValues.aiSummaryGroup)) {
        normalizedValues.aiSummaryGroup = sourceValues.aiSummaryGroup;
    }

    return normalizedValues;
}

function normalizeReportItemTypes(value: unknown): string[] {
    const values = Array.isArray(value) ? value : [value];
    const validValues = reportItemTypeOptions.map(option => option.value);

    return values
        .filter((itemType): itemType is string => typeof itemType === "string" && validValues.includes(itemType))
        .filter((itemType, index, itemTypes) => itemTypes.indexOf(itemType) === index);
}

function isValidAiSummaryGroupValue(value: string) {
    return value === AI_SUMMARY_UNGROUPED_GROUP_VALUE || AI_SUMMARY_GROUP_VALUE_PATTERN.test(value);
}

function buildAiSummaryGroupOptions(aiSummary?: VulReportAiSummary): AiSummaryGroupOption[] {
    if (aiSummary?.status !== "DONE") {
        return [];
    }

    const visibleGroups: VisibleAiSummaryGroup[] = aiSummary.itemGroups
        ?.map((group, index) => ({group, index}))
        .filter(({group}) => groupItemCount(group) >= 2) ?? [];

    const groups: AiSummaryGroupOption[] = visibleGroups
        .map(({group, index}) => {
            const title = group.title || `Group ${index + 1}`;
            const itemCount = groupItemCount(group);

            return {
                value: `${AI_SUMMARY_GROUP_VALUE_PREFIX}${index}`,
                label: `${title} (${formatInteger(itemCount)})`,
                title,
                itemCount,
                itemIds: group.itemIds,
                basis: group.basis
            };
        });

    const ungroupedItemCount = aiSummaryUngroupedItemCount(aiSummary, visibleGroups);

    if (aiSummary.inputItemCount !== undefined && ungroupedItemCount > 0) {
        groups.push({
            value: AI_SUMMARY_UNGROUPED_GROUP_VALUE,
            label: `Ungrouped (${formatInteger(ungroupedItemCount)})`,
            title: "Ungrouped",
            itemCount: ungroupedItemCount,
            basis: "Items not assigned to any AI summary group.",
            ungrouped: true
        });
    }

    return groups;
}

function aiSummaryUngroupedItemCount(aiSummary: VulReportAiSummary, visibleGroups: VisibleAiSummaryGroup[]) {
    const inputItemCount = aiSummary.inputItemCount ?? 0;
    const groupedItemCount = new Set(
        visibleGroups
            .flatMap(({group}) => group.itemIds ?? [])
            .filter((itemId): itemId is string => typeof itemId === "string")
    ).size;

    return Math.max(inputItemCount - groupedItemCount, 0);
}

function groupItemCount(group: VulReportAiSummaryGroup) {
    return group.itemCount ?? group.itemIds?.length ?? 0;
}

export default function VulReportDetail() {
    const {reportId} = useParams();

    return <VulReportDetailContent key={reportId}/>;
}
